import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Sparkles, RefreshCw, TrendingUp, TrendingDown, Info, AlertTriangle, AlertCircle, Lightbulb, Target } from 'lucide-react';
import { aiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const AnalisisIA = () => {
    const { tienePermiso } = useAuth();
    const puedeUsarIa = tienePermiso('usar_ia');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generando, setGenerando] = useState(false);

    const cargar = async () => {
        setLoading(true);
        try {
            const res = await aiAPI.obtenerAnalisisDashboard();
            setData(res.data);
        } catch (err) {
            console.error('Error cargar analisis:', err);
        } finally {
            setLoading(false);
        }
    };

    const regenerar = async () => {
        setGenerando(true);
        try {
            const res = await aiAPI.regenerarAnalisisDashboard();
            setData(res.data);
            toast.success('Análisis actualizado');
        } catch (err) {
            toast.error('Error: ' + err.message);
        } finally {
            setGenerando(false);
        }
    };

    useEffect(() => {
        if (puedeUsarIa) cargar();
        else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!puedeUsarIa) return null;

    if (loading) {
        return (
            <div className="card analisis-ia-card">
                <div className="loading">Cargando análisis...</div>
            </div>
        );
    }

    const formatFecha = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleString('es-PE', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getDestacadoIcon = (tipo) => {
        if (tipo === 'positivo') return <TrendingUp size={14} className="destacado-icon-positivo" />;
        if (tipo === 'negativo') return <TrendingDown size={14} className="destacado-icon-negativo" />;
        return <Info size={14} className="destacado-icon-info" />;
    };

    const getAlertaIcon = (nivel) => {
        if (nivel === 'alta') return <AlertCircle size={14} className="alerta-icon-alta" />;
        if (nivel === 'media') return <AlertTriangle size={14} className="alerta-icon-media" />;
        return <Info size={14} className="alerta-icon-baja" />;
    };

    return (
        <div className="card analisis-ia-card">
            <div className="card-header analisis-ia-header">
                <h2 className="ai-title">
                    <Sparkles size={20} className="ai-icon" />
                    Análisis Inteligente
                </h2>
                <button
                    className="btn ghost"
                    onClick={regenerar}
                    disabled={generando}
                    title={data ? 'Regenerar análisis con IA' : 'Generar análisis con IA'}
                >
                    {generando ? <RefreshCw size={14} className="spin" /> : <RefreshCw size={14} />}
                    {generando ? 'Generando...' : (data ? 'Refrescar' : 'Generar')}
                </button>
            </div>

            <div className="admin-content analisis-ia-content">
                {!data ? (
                    <div className="analisis-ia-empty">
                        <Sparkles size={32} style={{ opacity: 0.3 }} />
                        <p>No hay análisis generado. Click en "Generar" para crear el primero.</p>
                    </div>
                ) : (
                    <>
                        {data.resumen_ejecutivo && (
                            <div className="analisis-resumen">
                                <strong>Resumen:</strong> {data.resumen_ejecutivo}
                            </div>
                        )}

                        <div className="analisis-grid">
                            {data.destacados?.length > 0 && (
                                <div className="analisis-section analisis-box">
                                    <h4>Destacados</h4>
                                    <div className="analisis-destacados">
                                        {data.destacados.map((d, i) => (
                                            <div key={i} className={`analisis-destacado destacado-${d.tipo}`}>
                                                {getDestacadoIcon(d.tipo)}
                                                <span>{d.texto}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {data.categorias_top?.length > 0 && (
                                <div className="analisis-section analisis-box">
                                    <h4><Target size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Categorías</h4>
                                    <div className="analisis-categorias">
                                        {data.categorias_top.map((c, i) => (
                                            <div key={i} className="analisis-categoria">
                                                <div className="analisis-cat-header">
                                                    <span className="analisis-cat-nombre">{c.nombre}</span>
                                                    <span className={`analisis-cat-tasa ${c.tasa_aprobacion >= 60 ? 'alta' : c.tasa_aprobacion >= 40 ? 'media' : 'baja'}`}>
                                                        {c.tasa_aprobacion}%
                                                    </span>
                                                </div>
                                                {c.comentario && <div className="analisis-cat-comentario">{c.comentario}</div>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {data.alertas?.length > 0 && (
                                <div className="analisis-section analisis-box">
                                    <h4><AlertTriangle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Alertas</h4>
                                    <div className="analisis-alertas">
                                        {data.alertas.map((a, i) => (
                                            <div key={i} className={`analisis-alerta alerta-${a.nivel}`}>
                                                {getAlertaIcon(a.nivel)}
                                                <span>{a.texto}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {data.recomendaciones?.length > 0 && (
                                <div className="analisis-section analisis-box">
                                    <h4><Lightbulb size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Recomendaciones</h4>
                                    <ul className="analisis-recomendaciones">
                                        {data.recomendaciones.map((r, i) => (
                                            <li key={i}>{r}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className="analisis-footer">
                            <span className="analisis-footer-label">Generado:</span>
                            <span className="analisis-footer-fecha">
                                {data.generado_at ? formatFecha(data.generado_at) : 'Fecha no disponible'}
                            </span>
                            {data.generado_por_nombre && (
                                <span className="analisis-footer-meta"> · por {data.generado_por_nombre}</span>
                            )}
                            {data.modelo && (
                                <span className="analisis-footer-meta"> · {data.modelo}</span>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AnalisisIA;
